import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authHelper } from '../utils/authHelper';
import { 
  Smartphone, 
  GraduationCap, 
  Shield, 
  ArrowRight, 
  Loader2,
  Clock,
  CheckCircle2 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function LoginHub() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }

    fetchUserAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const fetchUserAccess = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/user-access`, {
        headers: authHelper.getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setAccess(data);
        
        // Auto-redirect logic
        if (data.is_admin) {
          // Admin has access to everything - show hub
          setLoading(false);
        } else if (data.has_app_mobile && !data.has_web_platform) {
          // Only has app mobile - redirect directly
          navigate('/dashboard', { replace: true });
        } else if (!data.has_app_mobile && data.has_web_platform) {
          // Only has web platform - redirect to LMS
          navigate('/lms', { replace: true });
        } else if (data.has_app_mobile && data.has_web_platform) {
          // Has both - show hub to choose
          setLoading(false);
        } else {
          // No access - redirect to subscription wall
          navigate('/dashboard', { replace: true });
        }
      } else {
        setError('Error al verificar acceso');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error fetching user access:', err);
      setError('Error de conexión');
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#02040A] flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#C5A14C] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#02040A] flex items-center justify-center p-6">
        <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-8 max-w-md w-full text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <button
            onClick={handleLogout}
            className="bg-white/10 text-white px-6 py-2 rounded-lg hover:bg-white/20 transition-colors"
          >
            Volver a Iniciar Sesión
          </button>
        </div>
      </div>
    );
  }

  if (!access) return null;

  return (
    <div className="min-h-screen bg-[#02040A] text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1E3A8A] to-[#02040A] border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#C5A14C] flex items-center justify-center font-bold text-[#02040A]">
                  M11
                </div>
                MÉTODO 11
              </h1>
              <p className="text-white/60 mt-2">
                Bienvenido, {user?.username || user?.name || user?.email?.split('@')[0]}
              </p>
            </div>

            <button
              onClick={handleLogout}
              className="text-sm text-white/60 hover:text-white transition-colors"
            >
              Cerrar Sesión
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-black mb-4">Selecciona una Plataforma</h2>
          <p className="text-lg text-white/60">
            Elige a dónde deseas acceder
          </p>
        </div>

        {/* Access Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* App Móvil Card */}
          {access.has_app_mobile && (
            <div
              onClick={() => navigate('/dashboard')}
              className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-8 hover:border-[#C5A14C]/40 hover:bg-white/10 transition-all duration-300 cursor-pointer group relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#C5A14C]/10 rounded-full blur-3xl" />
              
              <div className="relative z-10">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500/20 to-blue-600/20 border border-blue-500/30 flex items-center justify-center mb-6">
                  <Smartphone className="w-8 h-8 text-blue-400" />
                </div>

                <h3 className="text-2xl font-bold mb-3">App Móvil</h3>
                <p className="text-white/60 mb-6">
                  Gestión profesional de trading
                </p>

                {access.is_trial && (
                  <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 mb-4">
                    <Clock className="w-4 h-4 text-amber-500" />
                    <span className="text-sm text-amber-400">
                      {access.trial_days_left} días de prueba restantes
                    </span>
                  </div>
                )}

                <div className="flex items-center gap-2 text-[#C5A14C] font-semibold group-hover:gap-4 transition-all">
                  <span>Acceder</span>
                  <ArrowRight className="w-5 h-5" />
                </div>
              </div>
            </div>
          )}

          {/* Plataforma Web Card */}
          {access.has_web_platform && (
            <div
              onClick={() => navigate('/lms')}
              className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-8 hover:border-[#C5A14C]/40 hover:bg-white/10 transition-all duration-300 cursor-pointer group relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#C5A14C]/10 rounded-full blur-3xl" />
              
              <div className="relative z-10">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#C5A14C]/20 to-[#B08F3C]/20 border border-[#C5A14C]/30 flex items-center justify-center mb-6">
                  <GraduationCap className="w-8 h-8 text-[#C5A14C]" />
                </div>

                <h3 className="text-2xl font-bold mb-3">Plataforma Web</h3>
                <p className="text-white/60 mb-6">
                  Entrenamiento de Trading
                </p>

                <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2 mb-4">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  <span className="text-sm text-emerald-400">
                    Acceso de por vida
                  </span>
                </div>

                <div className="flex items-center gap-2 text-[#C5A14C] font-semibold group-hover:gap-4 transition-all">
                  <span>Acceder</span>
                  <ArrowRight className="w-5 h-5" />
                </div>
              </div>
            </div>
          )}

          {/* Admin Panel Card */}
          {access.is_admin && (
            <div
              onClick={() => navigate('/dashboard')}
              className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-8 hover:border-[#C5A14C]/40 hover:bg-white/10 transition-all duration-300 cursor-pointer group relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/10 rounded-full blur-3xl" />
              
              <div className="relative z-10">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500/20 to-purple-600/20 border border-purple-500/30 flex items-center justify-center mb-6">
                  <Shield className="w-8 h-8 text-purple-400" />
                </div>

                <h3 className="text-2xl font-bold mb-3">Panel Admin</h3>
                <p className="text-white/60 mb-6">
                  Gestión completa del sistema
                </p>

                <div className="flex items-center gap-2 bg-purple-500/10 border border-purple-500/20 rounded-lg px-3 py-2 mb-4">
                  <Shield className="w-4 h-4 text-purple-400" />
                  <span className="text-sm text-purple-300">
                    Acceso Administrador
                  </span>
                </div>

                <div className="flex items-center gap-2 text-[#C5A14C] font-semibold group-hover:gap-4 transition-all">
                  <span>Acceder</span>
                  <ArrowRight className="w-5 h-5" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Info Banner */}
        {access.is_trial && (
          <div className="mt-12 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <Clock className="w-6 h-6 text-amber-500 mt-1" />
              <div>
                <h4 className="font-bold text-lg mb-2">Período de Prueba Activo</h4>
                <p className="text-white/70">
                  Te quedan <span className="font-bold text-amber-400">{access.trial_days_left} días</span> de acceso gratuito a la App Móvil. 
                  Después de este período, necesitarás una suscripción de $3/mes para continuar.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
